// Components
import { ActionIcon, Select } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { FormField } from "@benchmark/ui/components/atomic";
import { FormGroup } from "@benchmark/ui/components/atomic";
import { PhoneInput } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import { PatientRelatedPersonContact } from "@benchmark/core/types";

/**
 * Props for ContactInput component.
 * @agentic @benchmark/ui
 */
type ContactInputProps = {
  index: number;
  contact: PatientRelatedPersonContact;
  onUpdate: (
    index: number,
    field: keyof PatientRelatedPersonContact,
    value: string,
  ) => void;
  onRemove: (index: number) => void;
  relationshipOptions: Array<{ value: string; label: string }>;
  getFieldError: (
    field: keyof PatientRelatedPersonContact,
  ) => string | undefined;
};

/**
 * Single contact input row for patient-related person (name, phone, relationship).
 * @agentic @benchmark/ui
 */
export const ContactInput = ({
  index,
  contact,
  onUpdate,
  onRemove,
  relationshipOptions,
  getFieldError,
}: ContactInputProps) => {
  return (
    <div>
      <FormGroup>
        <FormField label="First Name" required>
          <TextInput
            placeholder="Enter first name"
            autoComplete="off"
            value={contact["first-name"] || ""}
            onChange={(e) => onUpdate(index, "first-name", e.target.value)}
            aria-label={`Contact ${index + 1} first name`}
            error={getFieldError("first-name")}
          />
        </FormField>
        <FormField label="Last Name" required>
          <TextInput
            placeholder="Enter last name"
            autoComplete="off"
            value={contact["last-name"] || ""}
            onChange={(e) => onUpdate(index, "last-name", e.target.value)}
            aria-label={`Contact ${index + 1} last name`}
            error={getFieldError("last-name")}
          />
        </FormField>
      </FormGroup>

      <FormGroup>
        <FormField label="Phone" required>
          <PhoneInput
            placeholder="Enter phone number"
            autoComplete="off"
            value={contact.phone || ""}
            onChange={(e) => onUpdate(index, "phone", e.target.value)}
            aria-label={`Contact ${index + 1} phone number`}
            error={getFieldError("phone")}
          />
        </FormField>
        <FormField label="Relationship" required>
          <Select
            placeholder="Select relationship"
            data={relationshipOptions}
            searchable
            clearable
            value={contact.relationship || ""}
            onChange={(value) => onUpdate(index, "relationship", value || "")}
            aria-label={`Contact ${index + 1} relationship`}
            error={getFieldError("relationship")}
          />
        </FormField>
      </FormGroup>

      <FormField>
        <ActionIcon
          color="red"
          variant="subtle"
          onClick={() => onRemove(index)}
          aria-label={`Remove contact ${index + 1}`}
        >
          <IconTrash size={16} />
        </ActionIcon>
      </FormField>
    </div>
  );
};
